import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Divider
} from '@mui/material';

// Services
import { returnInventoryFromTechnician } from '../services/technicianInventoryService';
import { getWarehouses } from '../services/warehouseService';
import { getLocations } from '../services/locationService';

const ReturnInventoryDialog = ({ open, onClose, record }) => {
  const [formData, setFormData] = useState({
    return_quantity: 1,
    return_location_id: '',
    notes: ''
  });
  
  const [warehouses, setWarehouses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Load initial data
  useEffect(() => {
    if (open && record) {
      loadWarehouses();
      setFormData({
        return_quantity: record.quantity,
        return_location_id: '',
        notes: ''
      });
    }
  }, [open, record]);

  // Load locations when warehouse is selected
  useEffect(() => {
    if (selectedWarehouse) {
      loadLocations(selectedWarehouse);
    }
  }, [selectedWarehouse]);

  const loadWarehouses = async () => {
    try {
      const data = await getWarehouses();
      setWarehouses(data);
    } catch (err) {
      console.error('Error loading warehouses:', err);
    }
  };

  const loadLocations = async (warehouseId) => {
    try {
      const data = await getLocations({ warehouse_id: warehouseId });
      setLocations(data);
    } catch (err) {
      console.error('Error loading locations:', err);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);

      // Validate form
      if (!formData.return_quantity || !formData.return_location_id) {
        setError('Please fill in all required fields');
        return;
      }

      if (formData.return_quantity <= 0) {
        setError('Return quantity must be greater than 0');
        return;
      }

      if (formData.return_quantity > record.quantity) {
        setError(`Return quantity cannot exceed issued quantity (${record.quantity})`);
        return;
      }

      await returnInventoryFromTechnician(record.id, formData);
      handleClose();
    } catch (err) {
      console.error('Error returning inventory:', err);
      setError(err.message || 'Failed to return inventory');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      return_quantity: 1,
      return_location_id: '',
      notes: ''
    });
    setSelectedWarehouse('');
    setLocations([]);
    setError(null);
    onClose();
  };

  if (!record) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Return Inventory from Technician</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Record Information */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Current Record Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Technician
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {record.technician?.name} ({record.technician?.type})
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Part
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {record.part?.internal_part_number}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {record.part?.description}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Current Quantity
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {record.quantity}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Issued From
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {record.location?.warehouse?.name} - {record.location?.rack}-{record.location?.shelf}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Divider sx={{ mb: 3 }} />

          <Typography variant="h6" gutterBottom>
            Return Details
          </Typography>

          <Grid container spacing={3}>
            {/* Return Quantity */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Return Quantity *"
                type="number"
                value={formData.return_quantity}
                onChange={(e) => handleInputChange('return_quantity', parseInt(e.target.value) || 0)}
                inputProps={{ min: 1, max: record.quantity }}
                helperText={`Maximum: ${record.quantity}`}
              />
            </Grid>

            {/* Warehouse Selection */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Return to Warehouse *</InputLabel>
                <Select
                  value={selectedWarehouse}
                  label="Return to Warehouse *"
                  onChange={(e) => setSelectedWarehouse(e.target.value)}
                >
                  {warehouses.map((warehouse) => (
                    <MenuItem key={warehouse.id} value={warehouse.id}>
                      {warehouse.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Location Selection */}
            <Grid item xs={12}>
              <FormControl fullWidth disabled={!selectedWarehouse}>
                <InputLabel>Return to Location *</InputLabel>
                <Select
                  value={formData.return_location_id}
                  label="Return to Location *"
                  onChange={(e) => handleInputChange('return_location_id', e.target.value)}
                >
                  {locations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.rack}-{location.shelf}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Return Notes (Optional)"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Add any notes about the return..."
              />
            </Grid>
          </Grid>

          {/* Return Type Information */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="body2" color="info.dark">
              <strong>Note:</strong> {formData.return_quantity === record.quantity 
                ? 'This will be a full return and the record will be marked as returned.' 
                : 'This will be a partial return. The remaining quantity will stay with the technician.'}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={20} /> : null}
        >
          {submitting ? 'Processing...' : 'Return Inventory'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReturnInventoryDialog;
